<script setup lang="ts">
import { root } from 'postcss';
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';

// Variable Declarations
const showSections = ref(false);
const router = useRouter();


// Function Declarations

const goToHome = () => {
    router.push('/');
};


</script>

<template>
    <div class="relative flex flex-col md:flex-row w-full max-w-7xl lg:max-w-[90%] mx-auto h-auto sm:h-20 items-center justify-between px-4 sm:px-6">
        <!-- Logo - Desktop -->
        <div class="hidden md:flex items-center h-full flex-shrink-0">
            <picture>
                <source srcset="/images/JUIFull.webp" type="image/webp" />
                <img
                    class="h-16 w-auto cursor-pointer object-contain flex-shrink-0"
                    src="/images/JUIFull.png"
                    alt="JUI Logo"
                    loading="eager"
                    decoding="async"
                    fetchpriority="high"
                    @click="goToHome()"
                />
            </picture>
        </div>
        
        <!-- Logo - Mobile -->
        <div class="w-full flex justify-center md:hidden py-3">
            <picture>
                <source srcset="/images/JUIFull.webp" type="image/webp" />
                <img
                    class="h-12 cursor-pointer"
                    src="/images/JUIFull.png"
                    alt="JUI Logo"
                    loading="eager"
                    decoding="async"
                    fetchpriority="high"
                    @click="goToHome()"
                />
            </picture>
        </div>
        
        <!-- Navigation -->
        <nav class="w-full md:w-auto flex items-center justify-center md:justify-end">
            <div class="nav-links w-full max-w-2xl flex justify-between sm:justify-end sm:gap-6 lg:gap-8 items-center text-[#012331] font-satoshi text-sm sm:text-base">
                <router-link 
                    to="/" 
                    class="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg transition-all duration-300 hover:bg-[#e68c74]/10 hover:text-[#e68c74] active:scale-95"
                    active-class="text-[#e68c74]"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 sm:w-6 sm:h-6 inline-block sm:hidden">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                    </svg>
                    <span class="hidden sm:inline">Home</span>
                </router-link>
                
                <router-link 
                    to="/news" 
                    class="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg transition-all duration-300 hover:bg-[#e68c74]/10 hover:text-[#e68c74] active:scale-95"
                    active-class="text-[#e68c74]"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 sm:w-6 sm:h-6 inline-block sm:hidden">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 0 1-2.25 2.25M16.5 7.5V18a2.25 2.25 0 0 0 2.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 0 0 2.25 2.25h13.5M6 7.5h3v3H6v-3Z" />
                    </svg>
                    <span class="hidden sm:inline">News</span>
                </router-link>
                
                <div 
                    class="relative" 
                    @mouseenter="showSections = true" 
                    @mouseleave="showSections = false"
                >
                    <button 
                        class="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg transition-all duration-300 hover:bg-[#e68c74]/10 hover:text-[#e68c74] active:scale-95"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 sm:w-6 sm:h-6 inline-block sm:hidden">
                            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                        </svg>
                        <span class="hidden sm:inline">Sections</span>
                    </button>
                    
                    <Transition name="fade">
                        <div 
                            v-if="showSections" 
                            class="absolute top-full left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-0 mt-2 w-56 pt-2 z-50"
                        >
                            <div class="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                                <router-link 
                                    to="/sciencemedicine" 
                                    class="block px-6 py-3 text-[#012331] hover:bg-[#e68c74]/10 hover:text-[#e68c74] transition-colors duration-200"
                                >
                                    Science & Medicine
                                </router-link>
                                <router-link 
                                    to="/artshumanities" 
                                    class="block px-6 py-3 text-[#012331] hover:bg-[#e68c74]/10 hover:text-[#e68c74] transition-colors duration-200 border-t border-gray-100"
                                >
                                    Arts & Humanities
                                </router-link>
                            </div>
                        </div>
                    </Transition>
                </div>
                
                <router-link 
                    to="/guidelines" 
                    class="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg transition-all duration-300 hover:bg-[#e68c74]/10 hover:text-[#e68c74] active:scale-95"
                    active-class="text-[#e68c74]"
                >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 sm:w-6 sm:h-6 inline-block sm:hidden">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5" />
                    </svg>
                    <span class="hidden sm:inline">Guidelines</span>
                </router-link>
                
                <router-link 
                    to="/connect" 
                    class="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg transition-all duration-300 hover:bg-[#e68c74]/10 hover:text-[#e68c74] active:scale-95"
                    active-class="text-[#e68c74]"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 sm:w-6 sm:h-6 inline-block sm:hidden">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
                    </svg>
                    <span class="hidden sm:inline">Connect</span>
                </router-link>
            </div>
        </nav>
    </div>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.nav-links {
  font-family: 'SatoshiMedium', serif;
}
</style>

